<script lang="ts">
  import ThemeIcon from '../themes/ThemeIcon.svelte';
  import Button from '../ui/Button.svelte';
  import { getAppearance } from '../themes/appearance.svelte';
  interface Props {
    practice?: boolean;
    finishing?: boolean;
    showFinish?: boolean;
    finishDisabled?: boolean;
    canPlay?: boolean;
    showHint: () => void;
    finishRound: () => void;
    atHome: boolean;
    backDisabled?: boolean;
    joined: boolean;
    connected: boolean;
    reconnecting?: boolean;
    settingsOpen: boolean;
    back: () => void;
    home: () => void;
    settings: () => void;
  }
  let {
    practice = false,
    finishing = false,
    showFinish = false,
    finishDisabled = false,
    canPlay = false,
    showHint,
    finishRound,
    atHome,
    backDisabled = false,
    joined,
    connected,
    reconnecting = false,
    settingsOpen,
    back,
    home,
    settings,
  }: Props = $props();
  const appearance = getAppearance();
  const connectionLabel = $derived(connected ? 'Connected' : reconnecting ? 'Reconnecting' : 'Disconnected');
</script>

<nav class="page-nav" aria-label="Site navigation">
  {#if atHome}
    <h1 class="menu-title">
      {#if appearance.theme.id !== 'minimal'}<span class="theme-emblem"><ThemeIcon name="logo" /></span
        >{/if}Spellie
    </h1>
  {:else if showFinish}<Button
      variant="ghost"
      size="small"
      disabled={finishDisabled || finishing}
      aria-busy={finishing}
      onclick={finishRound}><ThemeIcon name="back" /> {finishing ? 'Finishing…' : 'Finish'}</Button
    >
  {:else}<Button variant="ghost" size="small" class="page-back" disabled={backDisabled} onclick={back}
      ><ThemeIcon name="back" /> Back</Button
    >{/if}
  <div class="page-nav-actions">
    {#if joined}
      <span class="nav-status">
        <span
          class:on={connected}
          class:reconnecting={!connected && reconnecting}
          class="conn"
          role="status"
          aria-label={connectionLabel}
          title={connectionLabel}
        ></span>
      </span>
    {/if}
    {#if practice}
      <div class="game-nav-actions">
        <Button size="small" disabled={!canPlay} onclick={showHint}>Show a Hint</Button>
      </div>
    {/if}
    <div class="nav-destinations">
      {#if !atHome}<Button variant="ghost" class="nav-icon" aria-label="Home" title="Home" onclick={home}>
          <ThemeIcon name="home" />
        </Button>{/if}
      <Button
        variant="ghost"
        class="nav-icon"
        aria-label="Settings"
        title="Settings"
        aria-haspopup="dialog"
        aria-expanded={settingsOpen}
        onclick={settings}
      >
        <ThemeIcon name="settings" />
      </Button>
    </div>
  </div>
</nav>

<style>
  .conn {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: transparent;
    border: 1px solid var(--theme-connection-disconnected);
    box-shadow: var(--theme-control-shadow);
    transition: background 0.3s;
    margin-right: 4px;
  }
  .conn.on {
    background: var(--theme-connection-connected);
    border-color: var(--theme-connection-connected);
    box-shadow: var(--theme-control-shadow);
  }
  .conn.reconnecting {
    background: var(--theme-connection-reconnecting);
    border-color: var(--theme-connection-reconnecting);
    animation: var(--theme-connection-reconnecting-animation);
  }

  /* Menu and site pages ------------------------------------------------ */
  .theme-emblem {
    font-size: 0.95em;
    animation: var(--theme-decoration-animation);
  }
  .menu-title {
    margin: 0;
    font-size: clamp(32px, 9vw, 44px);
    font-weight: var(--theme-heading-weight);
    text-shadow: var(--theme-title-shadow);
    text-transform: var(--theme-title-transform);
    color: var(--theme-foreground);
    letter-spacing: -1px;
    line-height: 1;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .page-nav {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    margin: 12px 0;
  }
  .page-nav :global(.page-back) {
    min-height: 44px;
    padding-left: 0;
  }
  .page-nav-actions {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 4px;
    flex-wrap: wrap;
    justify-content: flex-end;
    max-width: 100%;
  }
  .game-nav-actions,
  .nav-destinations {
    display: flex;
    gap: 4px;
  }
  .game-nav-actions :global(.btn) {
    padding: 8px;
  }
  .page-nav :global(.nav-icon),
  .nav-status {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    padding: 10px;
  }
  .nav-status .conn {
    margin: 0;
  }
  .page-nav :global(.nav-icon) {
    font-size: 22px;
  }
  @media (max-height: 560px) {
    :global(body.no-scroll) .page-nav {
      margin-block: 4px;
    }
  }
</style>
